import { useState, useEffect, FormEvent } from 'react';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import { reviews } from '@/mocks/reviews';

const CONTACT_SCHEMA = {
  '@context': 'https://schema.org',
  '@graph': [
    {
      '@type': 'LocalBusiness',
      '@id': `${import.meta.env.VITE_SITE_URL || 'https://example.com'}/#localbusiness`,
      name: 'MyAbode',
      url: import.meta.env.VITE_SITE_URL || 'https://example.com',
      telephone: '+18005550000',
      email: 'hello@myabode.com',
      address: {
        '@type': 'PostalAddress',
        streetAddress: '123 Property Lane, Suite 400',
        addressLocality: 'New York',
        addressRegion: 'NY',
        postalCode: '10001',
        addressCountry: 'US',
      },
      openingHoursSpecification: [
        {
          '@type': 'OpeningHoursSpecification',
          dayOfWeek: ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday'],
          opens: '09:00',
          closes: '19:00',
        },
        {
          '@type': 'OpeningHoursSpecification',
          dayOfWeek: ['Saturday', 'Sunday'],
          opens: '10:00',
          closes: '17:00',
        },
      ],
      aggregateRating: {
        '@type': 'AggregateRating',
        ratingValue: 4.9,
        bestRating: 5,
        worstRating: 1,
        reviewCount: 2450,
      },
    },
    {
      '@type': 'FAQPage',
      mainEntity: [
        {
          '@type': 'Question',
          name: 'How do I book a property viewing on MyAbode?',
          acceptedAnswer: {
            '@type': 'Answer',
            text: 'Simply browse our property listings, open a property detail page, and click "Book a Viewing". Choose your preferred date, time slot, and contact method — our team will confirm within 24 hours.',
          },
        },
        {
          '@type': 'Question',
          name: 'Can I save properties and compare them on MyAbode?',
          acceptedAnswer: {
            '@type': 'Answer',
            text: 'Yes! Click the heart icon on any property card to save it to your dashboard. Use the scale icon to add up to 3 properties to the Compare tool for a side-by-side comparison.',
          },
        },
        {
          '@type': 'Question',
          name: 'Does MyAbode list properties for both buying and renting?',
          acceptedAnswer: {
            '@type': 'Answer',
            text: 'Absolutely. MyAbode features premium property listings available for both sale and rent across major cities. Use the filters on the listings page to narrow down by listing type.',
          },
        },
      ],
    },
  ],
};

const ContactPage = () => {
  const [form, setForm] = useState({ name: '', email: '', phone: '', message: '', interest: 'buying' });
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [charCount, setCharCount] = useState(0);

  useEffect(() => {
    const id = 'schema-contact';
    let el = document.getElementById(id) as HTMLScriptElement | null;
    if (!el) {
      el = document.createElement('script');
      el.id = id;
      el.type = 'application/ld+json';
      document.head.appendChild(el);
    }
    el.textContent = JSON.stringify(CONTACT_SCHEMA);
    document.title = 'Contact MyAbode | Reviews, Office & Property Enquiries';
    const desc = document.querySelector('meta[name="description"]');
    if (desc) desc.setAttribute('content', 'Get in touch with MyAbode. Read verified customer reviews, find our office address, and send us a property enquiry. We respond within 24 hours.');
    return () => {
      const s = document.getElementById(id);
      if (s) s.remove();
    };
  }, []);

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (charCount > 500) return;
    setSubmitting(true);
    try {
      const body = new URLSearchParams({
        name: form.name,
        email: form.email,
        phone: form.phone,
        message: form.message,
        interest: form.interest,
      });
      await fetch('https://readdy.ai/api/form/d7dpm49noju8nk5a1e10', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: body.toString(),
      });
      setSubmitted(true);
    } catch {
      setSubmitted(true);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-stone-50">
      <Navbar />
      <div className="pt-[72px]">
        {/* Header */}
        <div className="bg-stone-900 py-14 px-4 md:px-8 text-center">
          <p className="text-amber-400 text-xs font-semibold uppercase tracking-widest mb-3">Reviews &amp; Contact</p>
          <h1 className="text-4xl md:text-5xl font-black text-white mb-3">We&apos;d Love to Hear From You</h1>
          <p className="text-stone-400 text-sm max-w-xl mx-auto">Whether you&apos;re buying, renting, or just exploring — our team is here to help every step of the way.</p>
        </div>

        {/* Reviews Section */}
        <section className="py-16 bg-white">
          <div className="max-w-7xl mx-auto px-4 md:px-8">
            <div className="grid lg:grid-cols-3 gap-10 items-start">
              {/* Rating Summary */}
              <div className="bg-amber-50 rounded-2xl p-8 text-center border border-amber-100">
                <p className="text-7xl font-black text-stone-900 leading-none">4.9</p>
                <div className="flex justify-center gap-1 my-3">
                  {[1,2,3,4,5].map(i => <i key={i} className="ri-star-fill text-amber-400 text-xl"></i>)}
                </div>
                <p className="text-stone-500 text-sm">Based on 2,450+ reviews</p>
                <div className="mt-6 space-y-2">
                  {[5,4,3,2,1].map(star => (
                    <div key={star} className="flex items-center gap-2">
                      <span className="text-xs text-stone-500 w-4">{star}</span>
                      <i className="ri-star-fill text-amber-400 text-xs"></i>
                      <div className="flex-1 h-1.5 bg-stone-200 rounded-full overflow-hidden">
                        <div className="h-full bg-amber-400 rounded-full" style={{ width: star === 5 ? '78%' : star === 4 ? '15%' : star === 3 ? '5%' : '1%' }}></div>
                      </div>
                      <span className="text-xs text-stone-400 w-8">{star === 5 ? '78%' : star === 4 ? '15%' : star === 3 ? '5%' : star === 2 ? '1%' : '1%'}</span>
                    </div>
                  ))}
                </div>
                <div className="mt-5 flex flex-col gap-2">
                  <div className="flex items-center justify-center gap-2 text-xs text-stone-500">
                    <i className="ri-verified-badge-line text-green-500"></i> Verified Reviews
                  </div>
                  <div className="flex items-center justify-center gap-2 text-xs text-stone-500">
                    <i className="ri-google-line text-amber-500"></i> Google Rated
                  </div>
                </div>
              </div>

              {/* Review Cards */}
              <div className="lg:col-span-2 space-y-4">
                {reviews.map(review => (
                  <div key={review.id} className="bg-stone-50 rounded-2xl p-5 border border-stone-100">
                    <div className="flex items-start gap-3">
                      <div className="w-10 h-10 rounded-full overflow-hidden bg-stone-200 flex-shrink-0">
                        <img src={review.avatar} alt={review.reviewer} className="w-full h-full object-cover object-top" />
                      </div>
                      <div className="flex-1">
                        <div className="flex items-center justify-between flex-wrap gap-2">
                          <div>
                            <p className="font-semibold text-stone-800 text-sm">{review.reviewer}</p>
                            <p className="text-stone-400 text-xs">{review.location}</p>
                          </div>
                          <div className="flex items-center gap-1">
                            {[1,2,3,4,5].map(i => (
                              <i key={i} className={`ri-star-fill text-xs ${i <= review.rating ? 'text-amber-400' : 'text-stone-200'}`}></i>
                            ))}
                            <span className="text-stone-400 text-xs ml-1">{review.date}</span>
                          </div>
                        </div>
                        <p className="text-stone-600 text-sm leading-relaxed mt-2">"{review.text}"</p>
                        <p className="text-stone-400 text-xs mt-2 flex items-center gap-1">
                          <i className="ri-home-line text-amber-400"></i> {review.property}
                        </p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* Contact Section */}
        <section className="py-16 bg-stone-50">
          <div className="max-w-7xl mx-auto px-4 md:px-8">
            <div className="grid lg:grid-cols-2 gap-10">
              {/* Form */}
              <div className="bg-white rounded-2xl p-8 border border-stone-100">
                <h2 className="text-2xl font-black text-stone-900 mb-1">Let&apos;s Find Your Perfect Home</h2>
                <p className="text-stone-400 text-sm mb-6">Fill in the form and our team will get back to you within 24 hours.</p>

                {submitted ? (
                  <div className="text-center py-10">
                    <div className="w-14 h-14 flex items-center justify-center bg-green-50 rounded-full mx-auto mb-4">
                      <i className="ri-check-line text-2xl text-green-500"></i>
                    </div>
                    <h3 className="font-bold text-stone-800 mb-2">Message Sent!</h3>
                    <p className="text-stone-500 text-sm">Thanks for reaching out. We&apos;ll be in touch shortly.</p>
                  </div>
                ) : (
                  <form data-readdy-form id="contact-form" onSubmit={handleSubmit} className="space-y-4">
                    <div className="grid sm:grid-cols-2 gap-4">
                      <input name="name" type="text" value={form.name} onChange={e => setForm({...form, name: e.target.value})} placeholder="Full Name *" required className="w-full px-4 py-3 border border-stone-200 rounded-xl text-sm focus:outline-none focus:border-amber-400 text-stone-700" />
                      <input name="email" type="email" value={form.email} onChange={e => setForm({...form, email: e.target.value})} placeholder="Email Address *" required className="w-full px-4 py-3 border border-stone-200 rounded-xl text-sm focus:outline-none focus:border-amber-400 text-stone-700" />
                    </div>
                    <input name="phone" type="tel" value={form.phone} onChange={e => setForm({...form, phone: e.target.value})} placeholder="Phone Number" className="w-full px-4 py-3 border border-stone-200 rounded-xl text-sm focus:outline-none focus:border-amber-400 text-stone-700" />
                    <div>
                      <p className="text-stone-600 text-sm font-medium mb-2">I&apos;m interested in:</p>
                      <div className="flex gap-3 flex-wrap">
                        {['buying', 'renting', 'selling'].map(opt => (
                          <label key={opt} className="flex items-center gap-2 cursor-pointer">
                            <input type="radio" name="interest" value={opt} checked={form.interest === opt} onChange={() => setForm({...form, interest: opt})} className="accent-amber-500" />
                            <span className="text-stone-600 text-sm capitalize">{opt}</span>
                          </label>
                        ))}
                      </div>
                    </div>
                    <div className="relative">
                      <textarea
                        name="message"
                        value={form.message}
                        onChange={e => { if (e.target.value.length <= 500) { setForm({...form, message: e.target.value}); setCharCount(e.target.value.length); } }}
                        placeholder="Your message..."
                        rows={4}
                        className="w-full px-4 py-3 border border-stone-200 rounded-xl text-sm focus:outline-none focus:border-amber-400 text-stone-700 resize-none"
                      />
                      <span className={`absolute bottom-3 right-3 text-xs ${charCount > 450 ? 'text-red-400' : 'text-stone-300'}`}>{charCount}/500</span>
                    </div>
                    <button type="submit" disabled={submitting || charCount > 500} className="w-full py-3.5 bg-amber-500 hover:bg-amber-600 disabled:bg-stone-200 text-white font-bold rounded-xl transition-colors cursor-pointer whitespace-nowrap flex items-center justify-center gap-2">
                      {submitting ? 'Sending...' : <><i className="ri-send-plane-line"></i> Send Message</>}
                    </button>
                  </form>
                )}
              </div>

              {/* Contact Info */}
              <div className="bg-stone-900 rounded-2xl p-8 text-white">
                <h2 className="text-2xl font-black mb-6">Get in Touch</h2>
                <div className="space-y-5 mb-8">
                  {[
                    { icon: 'ri-map-pin-line', title: 'Office', text: '123 Property Lane, Suite 400\nNew York, NY 10001' },
                    { icon: 'ri-phone-line', title: 'Phone', text: '+1 (800) 555-ABODE\n+1 (212) 555-0100' },
                    { icon: 'ri-mail-line', title: 'Email', text: 'hello@myabode.com\nsupport@myabode.com' },
                    { icon: 'ri-time-line', title: 'Office Hours', text: 'Mon–Fri: 9AM – 7PM\nSat–Sun: 10AM – 5PM' },
                  ].map(item => (
                    <div key={item.title} className="flex gap-4">
                      <div className="w-10 h-10 flex items-center justify-center bg-amber-500/20 rounded-xl flex-shrink-0">
                        <i className={`${item.icon} text-amber-400`}></i>
                      </div>
                      <div>
                        <p className="font-semibold text-sm mb-0.5">{item.title}</p>
                        <p className="text-stone-400 text-xs leading-relaxed whitespace-pre-line">{item.text}</p>
                      </div>
                    </div>
                  ))}
                </div>
                <div>
                  <p className="text-stone-400 text-xs mb-3">Follow us</p>
                  <div className="flex gap-3">
                    {['ri-instagram-line', 'ri-facebook-line', 'ri-linkedin-line', 'ri-twitter-x-line'].map(icon => (
                      <a key={icon} href="#" rel="nofollow" className="w-9 h-9 flex items-center justify-center rounded-full bg-white/10 hover:bg-amber-500 text-white transition-colors cursor-pointer">
                        <i className={`${icon} text-sm`}></i>
                      </a>
                    ))}
                  </div>
                </div>
                {/* Map */}
                <div className="mt-6 rounded-xl overflow-hidden h-36">
                  <iframe
                    src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3022.215573291865!2d-73.98784492346177!3d40.75798657138946!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x89c25855c6480299%3A0x55194ec5a1ae072e!2sTimes%20Square!5e0!3m2!1sen!2sus!4v1712000000000!5m2!1sen!2sus"
                    width="100%"
                    height="100%"
                    style={{ border: 0 }}
                    allowFullScreen
                    loading="lazy"
                    title="MyAbode Office Location"
                  ></iframe>
                </div>
              </div>
            </div>
          </div>
        </section>
      </div>
      <Footer />
    </div>
  );
};

export default ContactPage;
